import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { VeracordLogo } from '@/components/ui/VeracordLogo';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().trim().email({ message: 'Invalid email address' }).max(255),
  password: z.string().min(1, { message: 'Password is required' }).max(72),
});

export default function Auth() {
  const { user, profile, loading, profileError, signIn } = useAuth();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const rawRedirect = searchParams.get('redirect');
  const safeRedirect = (rawRedirect?.startsWith('/') && !rawRedirect.startsWith('//')) ? rawRedirect : '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (!loading && user && profile) {
    return <Navigate to={safeRedirect} replace />;
  }

  if (!loading && user && (profileError || !profile)) {
    return <Navigate to="/no-access" replace />;
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    const validation = loginSchema.safeParse({ email, password });
    if (!validation.success) {
      toast({ variant: 'destructive', title: 'Validation Error', description: validation.error.errors[0]?.message || 'Invalid input' });
      return;
    }
    setIsSubmitting(true);
    const { error } = await signIn(email, password);
    setIsSubmitting(false);
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Login Failed',
        description: error.message === 'Invalid login credentials'
          ? 'Invalid email or password. Please try again.'
          : error.message,
      });
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = resetEmail.trim();
    if (!trimmed || !z.string().email().safeParse(trimmed).success) {
      toast({ variant: 'destructive', title: 'Invalid Email', description: 'Please enter a valid email address.' });
      return;
    }
    setIsResetting(true);
    try {
      const { error: fnError } = await supabase.functions.invoke('send-auth-email', {
        body: { type: 'recovery', email: trimmed, redirect_to: `${window.location.origin}/reset-password` },
      });
      if (fnError) throw fnError;
    } catch (err) {
      console.error('Password reset email error:', err);
    }
    setIsResetting(false);
    setResetSent(true);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 bg-muted">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block">
            <VeracordLogo size="lg" color="#02075d" />
          </a>
        </div>

        {/* Card */}
        <div className="rounded-2xl border bg-card text-card-foreground shadow-sm p-8">
          {/* Heading */}
          <div className="mb-6 text-center">
            <h2 className="text-xl font-semibold text-foreground" style={{ fontFamily: "'Source Serif Pro', serif" }}>
              {showForgotPassword ? 'Reset Password' : 'Welcome Back'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {showForgotPassword
                ? 'Enter your email to receive a password reset link'
                : 'Log in to access your compliance dashboard'}
            </p>
          </div>

          {showForgotPassword ? (
            resetSent ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <p className="text-sm text-center font-medium text-foreground">Reset link sent!</p>
                <p className="text-sm text-muted-foreground text-center leading-relaxed">
                  If an account exists for that email, you'll receive a password reset link. Check your spam folder if you don't see it.
                </p>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => { setResetSent(false); setResetEmail(''); }}
                    className="px-4 py-2 rounded-lg border text-muted-foreground hover:text-foreground hover:border-border text-sm transition-all"
                  >
                    Resend Email
                  </button>
                  <button
                    onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(''); }}
                    className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-all"
                  >
                    Back to Log In
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    disabled={isResetting}
                    className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 transition-all text-sm disabled:opacity-50"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isResetting}
                  className="w-full py-3 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isResetting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto transition-colors"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Back to Log In
                </button>
              </form>
            )
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2.5 rounded-lg bg-background border border-input text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 transition-all text-sm disabled:opacity-50"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-foreground">Password</label>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-2.5 pr-10 rounded-lg bg-background border border-input text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 transition-all text-sm disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Log In'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-muted-foreground text-xs mt-6">
          Need help? Email{' '}
          <a href="mailto:support@veracord.co" className="text-foreground/60 hover:text-foreground underline">
            support@veracord.co
          </a>
        </p>
      </div>
    </div>
  );
}
